import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Login as LoginComponent } from 'utto-react-components';
import Footer from './footer';
import { withTracker } from 'utils';
import { useReduxSelector, useReduxAction, useRouter } from 'hooks';
import { getLogin } from 'ducks/auth/selectors';
import { loginActions } from 'ducks/auth/actions';
import history from 'services/history';
import Logo from 'logo.svg';
import useStyles from 'pages/styles';
import RDLogo from '../whitelabel/RadioDetection/menu.logo.png';
import RegisteredTM from 'components/registeredTM';
import { getBranding, isLpmRadioDetection } from 'whitelabel';

const Login = props => {
  const classes = useStyles();
  const { location } = useRouter();
  const [locationError, setLocationError] = useState(location?.state?.error);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const { error, pending } = useReduxSelector(getLogin);
  const login = useReduxAction(loginActions.request);

  const { hostname, search } = window.location;
  const urlParams = new URLSearchParams(search);
  const parts = hostname.split('.');
  const subdomain = parts.length ? parts[0] : '';

  const isLpmRadioDetectionDomain = isLpmRadioDetection();

  // Detect theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkTheme(mediaQuery.matches);

    const handleThemeChange = (e) => {
      setIsDarkTheme(e.matches);
    };

    mediaQuery.addEventListener('change', handleThemeChange);
    return () => mediaQuery.removeEventListener('change', handleThemeChange);
  }, []);

  const logoMap = {
    default: {
      loginLogo: Logo,
      loginText: 'UTTO Locate Performance Management',
      loginTextColor: '#fff'
    },
    lpm: {
      loginLogo: RDLogo,
      loginText: '<span style="color: #efa768;">L</span><span style="color: #3d8ac9;">ocate</span> <span style="color: #efa768;">P</span><span style="color: #3d8ac9;">erformance</span> <span style="color: #efa768;">M</span><span style="color: #3d8ac9;">anagement<sup>™</sup></span>',
      loginTextColor: '#002F6C',
      favicon: require('../whitelabel/RadioDetection/favicon-light.ico'),
      faviconLight: require('../whitelabel/RadioDetection/favicon-light.ico'),
      faviconDark: require('../whitelabel/RadioDetection/favicon-dark.ico'),
      tabTitle: 'LPM',
      subTitle: '<span style="color: #3d8ac9; font-size: 18px;">Locate Assurance<sup>®</sup></span><br/>Powered by <strong>UTTO</strong>',
      subTitleStyle: {
        color: '#002F6C',
        fontSize: '14px',
        fontWeight: 'normal',
        textAlign: 'center',
        marginTop: '0.5rem',
        backgroundColor: '#e3f2fd',
        padding: '8px 12px',
        borderRadius: '4px',
        border: '1px solid #bbdefb'
      }
    }
  };

  const LoginLogo = (client) => {
    // Use LPM styling only for the specific RadioDetection domain or URL parameter
    const clientKey = isLpmRadioDetectionDomain ? 'lpm' : 'default';
    const partnerLogin = logoMap[clientKey];

    return (
      <>
      <div>
        <img src={partnerLogin.loginLogo} alt={`${client.client} logo`} />
      </div>
      <div
        style={{
        color: partnerLogin.loginTextColor,
        fontSize: '20px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '0.1rem'
        }}
      >
        <span dangerouslySetInnerHTML={{ __html: partnerLogin.loginText }} />
        {partnerLogin.loginText && partnerLogin.loginText.trim() !== ''}
      </div>
      {partnerLogin.subTitle && (
        <div
          style={partnerLogin.subTitleStyle || {
            color: partnerLogin.loginTextColor,
            fontSize: '14px',
            fontWeight: 'normal',
            textAlign: 'center',
            marginTop: '0.5rem'
          }}
          dangerouslySetInnerHTML={{ __html: partnerLogin.subTitle }}
        />
      )}
      </>
    );
  };

  const BrowserTab = (client) => {
    // Use LPM styling only for the specific RadioDetection domain or URL parameter
    const clientKey = isLpmRadioDetectionDomain ? 'lpm' : 'default';
    const partnerLogin = logoMap[clientKey];

    // Get appropriate favicon based on theme
    const getFavicon = () => {
      if (isDarkTheme && partnerLogin.faviconDark) {
        return partnerLogin.faviconDark;
      }
      return partnerLogin.faviconLight || partnerLogin.favicon;
    };

    return (
      <Helmet>
        <title>{partnerLogin.tabTitle || 'UTTO'} | Sign in</title>
        {getFavicon() && (
          <link rel="icon" type="image/x-icon" href={getFavicon()} />
        )}
      </Helmet>
    );
  };

  const onForgotPassword = useCallback(
    () => history.push('/account/forgotpassword'),
    []
  );
  const onLogin = useCallback(
    ({ email, password }) => {
      setLocationError(null);
      login({ email: email.toLowerCase(), password });
    },
    [login]
  );

  return (
    <div className={classes.dialogContainer}>
      <BrowserTab client={subdomain} />
      <LoginComponent
        title=" "
        logo={<LoginLogo client={subdomain} />}
        loading={pending}
        error={error || locationError}
        onSubmit={onLogin}
        onForgotPassword={onForgotPassword}
      />
      <Footer classname={classes.footer} isLoginPage={isLpmRadioDetectionDomain} />
    </div>
  );
};

export default withTracker(Login);
